import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ShieldCheck, ShieldAlert, Plus, Trash2, Save, Loader2,
  CheckCircle, XCircle, Clock, Building2, Mail, Calendar,
  GripVertical, AlertCircle, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import api from '../../lib/api';

const ProducerVerificationPage = () => {
  const [activeTab, setActiveTab] = useState('requirements');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Requirements state
  const [settings, setSettings] = useState({
    is_enabled: true,
    instructions: '',
    requirements: []
  });
  
  // Requests state
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, requestsRes] = await Promise.all([
        api.get('/settings/verification-requirements'),
        api.get('/settings/verification-requests')
      ]);
      setSettings(settingsRes.data);
      setRequests(requestsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/settings/verification-requirements', settings);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const addRequirement = () => {
    setSettings(prev => ({
      ...prev,
      requirements: [
        ...prev.requirements,
        {
          id: `req_${Date.now()}`,
          title: '',
          description: '',
          is_required: true,
          order: prev.requirements.length
        }
      ]
    }));
  };

  const updateRequirement = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      requirements: prev.requirements.map((req, i) => 
        i === index ? { ...req, [field]: value } : req
      )
    }));
  };

  const removeRequirement = (index) => {
    setSettings(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };

  const handleReview = async (action) => {
    if (!selectedRequest) return;
    setProcessing(true);
    
    try {
      await api.put(`/settings/verification-requests/${selectedRequest.id}?action=${action}${action === 'reject' ? `&rejection_reason=${encodeURIComponent(rejectionReason)}` : ''}`);
      toast.success(action === 'approve' ? 'Productora verificada' : 'Solicitud rechazada');
      setShowReviewModal(false);
      setSelectedRequest(null);
      setRejectionReason('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar');
    } finally {
      setProcessing(false);
    }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="flex justify-center items-center h-64">
            <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <div className="p-6 max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <ShieldCheck className="text-amber-500" />
              Verificación de Productoras
            </h1>
            <p className="text-zinc-400 mt-1">
              Configura los requisitos y gestiona las solicitudes de verificación
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-zinc-800 pb-2">
            <button
              onClick={() => setActiveTab('requirements')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'requirements' 
                  ? 'bg-amber-500/20 text-amber-500' 
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Requisitos
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'requests' 
                  ? 'bg-amber-500/20 text-amber-500' 
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Solicitudes
              {pendingCount > 0 && (
                <span className="px-2 py-0.5 bg-amber-500 text-black text-xs font-bold rounded-full">
                  {pendingCount}
                </span>
              )}
            </button>
          </div>

          {/* Requirements Tab */}
          {activeTab === 'requirements' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Enable/Disable */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-white">Sistema de verificación</h3>
                    <p className="text-zinc-400 text-sm">
                      Mostrar opción de verificación a productoras no verificadas
                    </p>
                  </div>
                  <Switch
                    checked={settings.is_enabled}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, is_enabled: checked }))}
                  />
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <Label className="text-white mb-2 block">Instrucciones para productoras</Label>
                <Textarea
                  value={settings.instructions}
                  onChange={(e) => setSettings(prev => ({ ...prev, instructions: e.target.value }))}
                  placeholder="Texto que verán las productoras antes de los requisitos..."
                  className="bg-zinc-800 border-zinc-700 text-white"
                  rows={3}
                />
              </div>

              {/* Requirements List */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-white">Requisitos de verificación</h3>
                  <Button onClick={addRequirement} size="sm" className="bg-amber-500 hover:bg-amber-600 text-black">
                    <Plus size={16} className="mr-1" />
                    Agregar
                  </Button>
                </div>

                <div className="space-y-3">
                  {settings.requirements?.map((req, index) => (
                    <div key={req.id} className="bg-zinc-800 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-amber-500 font-bold text-sm">{index + 1}</span>
                        </div>
                        <div className="flex-1 space-y-3">
                          <Input
                            value={req.title}
                            onChange={(e) => updateRequirement(index, 'title', e.target.value)}
                            placeholder="Título del requisito"
                            className="bg-zinc-900 border-zinc-700"
                          />
                          <Input
                            value={req.description}
                            onChange={(e) => updateRequirement(index, 'description', e.target.value)}
                            placeholder="Descripción"
                            className="bg-zinc-900 border-zinc-700 text-sm"
                          />
                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 text-sm text-zinc-400">
                              <input
                                type="checkbox"
                                checked={req.is_required}
                                onChange={(e) => updateRequirement(index, 'is_required', e.target.checked)}
                                className="rounded border-zinc-600"
                              />
                              Obligatorio
                            </label>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeRequirement(index)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {settings.requirements?.length === 0 && (
                    <div className="text-center py-8 text-zinc-500">
                      No hay requisitos configurados. Agregá al menos uno.
                    </div>
                  )}
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button onClick={saveSettings} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black">
                  {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                  Guardar cambios
                </Button>
              </div>
            </motion.div>
          )}

          {/* Requests Tab */}
          {activeTab === 'requests' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              {/* Filter Tabs */}
              <div className="flex gap-2 mb-4">
                {['all', 'pending', 'approved', 'rejected'].map((filter) => (
                  <button
                    key={filter}
                    className="px-3 py-1 text-sm rounded-full bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
                  >
                    {filter === 'all' ? 'Todas' : 
                     filter === 'pending' ? 'Pendientes' :
                     filter === 'approved' ? 'Aprobadas' : 'Rechazadas'}
                  </button>
                ))}
              </div>

              {/* Requests List */}
              <div className="space-y-3">
                {requests.map((request) => (
                  <div key={request.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {request.producer?.branding?.logo_url ? (
                          <img 
                            src={request.producer.branding.logo_url} 
                            alt={request.producer_name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-zinc-800 rounded-lg flex items-center justify-center">
                            <Building2 className="text-zinc-500" size={24} />
                          </div>
                        )}
                        <div>
                          <h4 className="font-bold text-white">{request.producer_name}</h4>
                          <p className="text-zinc-400 text-sm flex items-center gap-1">
                            <Mail size={12} />
                            {request.producer_email}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                          request.status === 'pending' ? 'bg-amber-500/20 text-amber-500' :
                          request.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                          'bg-red-500/20 text-red-500'
                        }`}>
                          {request.status === 'pending' ? 'Pendiente' :
                           request.status === 'approved' ? 'Aprobada' : 'Rechazada'}
                        </div>
                        
                        {request.status === 'pending' && (
                          <Button
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowReviewModal(true);
                            }}
                            size="sm"
                            className="bg-amber-500 hover:bg-amber-600 text-black"
                          >
                            <Eye size={14} className="mr-1" />
                            Revisar
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center gap-4 text-xs text-zinc-500">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(request.created_at).toLocaleDateString('es-AR')}
                      </span>
                      {request.message && (
                        <span className="text-zinc-400">
                          "{request.message.substring(0, 50)}..."
                        </span>
                      )}
                    </div>
                  </div>
                ))}

                {requests.length === 0 && (
                  <div className="text-center py-12 text-zinc-500">
                    <ShieldAlert size={48} className="mx-auto mb-3 opacity-50" />
                    <p>No hay solicitudes de verificación</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Review Modal */}
          <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white">Revisar solicitud</DialogTitle>
              </DialogHeader>
              
              {selectedRequest && (
                <div className="py-4 space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-zinc-800 rounded-lg">
                    {selectedRequest.producer?.branding?.logo_url ? (
                      <img 
                        src={selectedRequest.producer.branding.logo_url} 
                        alt={selectedRequest.producer_name}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-zinc-700 rounded-lg flex items-center justify-center">
                        <Building2 className="text-zinc-500" size={32} />
                      </div>
                    )}
                    <div>
                      <h4 className="font-bold text-white text-lg">{selectedRequest.producer_name}</h4>
                      <p className="text-zinc-400 text-sm">{selectedRequest.producer_email}</p>
                    </div>
                  </div>
                  
                  {selectedRequest.message && (
                    <div className="p-3 bg-zinc-800/50 rounded-lg">
                      <p className="text-zinc-400 text-sm">Mensaje de la productora:</p>
                      <p className="text-white mt-1">{selectedRequest.message}</p>
                    </div>
                  )}
                  
                  {/* Producer Stats */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 bg-zinc-800 rounded-lg text-center">
                      <p className="text-2xl font-bold text-white">{selectedRequest.producer?.total_events || 0}</p>
                      <p className="text-xs text-zinc-500">Eventos</p>
                    </div>
                    <div className="p-3 bg-zinc-800 rounded-lg text-center">
                      <p className="text-2xl font-bold text-white">{selectedRequest.producer?.total_tickets_sold || 0}</p>
                      <p className="text-xs text-zinc-500">Tickets</p>
                    </div>
                    <div className="p-3 bg-zinc-800 rounded-lg text-center">
                      <p className={`text-lg font-bold ${selectedRequest.producer?.bank_info?.cbu ? 'text-green-400' : 'text-red-400'}`}>
                        {selectedRequest.producer?.bank_info?.cbu ? 'Sí' : 'No'}
                      </p>
                      <p className="text-xs text-zinc-500">Datos bancarios</p>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <Label className="text-red-400 text-sm">Motivo de rechazo (si aplica)</Label>
                    <Textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Solo si vas a rechazar la solicitud..."
                      className="bg-zinc-900 border-zinc-700 mt-2"
                      rows={2}
                    />
                  </div>
                </div>
              )}
              
              <DialogFooter className="flex gap-2">
                <Button
                  onClick={() => handleReview('reject')}
                  disabled={processing}
                  variant="outline"
                  className="border-red-500 text-red-400 hover:bg-red-500/10"
                >
                  <XCircle size={16} className="mr-2" />
                  Rechazar
                </Button>
                <Button
                  onClick={() => handleReview('approve')}
                  disabled={processing}
                  className="bg-green-500 hover:bg-green-600 text-white"
                >
                  {processing ? <Loader2 size={16} className="animate-spin mr-2" /> : <CheckCircle size={16} className="mr-2" />}
                  Aprobar y verificar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default ProducerVerificationPage;
